import React, { useEffect, useState } from "react";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  setPage,
  setRowsPerPage,
  setSearchValue,
  setItems,
  setTotalItems,
  setIsTableLoading,
} from "src/redux/slices/StudentTestimonialSlice";
import StudentTestimonialListing from "./StudentTestimonialListing";
import AddStudentTestimonialFormWrapper from "../Add/AddStudentTestimonialFormWrapper";
import EditStudentTestimonialFormWrapper from "../Edit/EditStudentTestimonialFormWrapper";
import {
  useDeleteTestimonialMutation,
  useGetAllTestimonialWithPaginationQuery,
} from "src/services/StudentTestimonialServices";

const StudentTestimonialListingWrapper = () => {
  const dispatch = useDispatch();

  const [isOpenAddFrom, setIsOpenAddForm] = useState(false);
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [deleteTestimonial] = useDeleteTestimonialMutation();

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.studentTestimonial);

  const { data, isLoading, isFetching } =
    useGetAllTestimonialWithPaginationQuery({
      limit: rowsPerPage,
      searchValue: searchValue,
      params: ["image", "video", "name", "text", "currentCompany"],
      page: page,
      filterBy: [],
      dateFilter: {},
      orderBy: "createdAt",
      orderByValue: -1,
      isPaginationRequired: true,
    });

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
      dispatch(setIsTableLoading(false));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (testimonialId: string) => {
    return [
      {
        label: (
          <div className="flex items-center gap-2 text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setSelectedId(testimonialId);
          setIsOpenEditForm(true);
        },
        actionName: UserModuleActionTypes.EDIT,
        moduleName: UserModuleNameTypes.TESTIMONIALS,
      },
      {
        label: (
          <div className="flex items-center gap-2 font-semibold text-red-600">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to Delete this Testimonial ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result?.isConfirmed) {
                deleteTestimonial({ id: testimonialId }).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.DELETE,
        moduleName: UserModuleNameTypes.TESTIMONIALS,
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "image",
      headerName: "Student image",
      extraClasses: "w-[120px]",
      renderCell: (row: any) => (
        // eslint-disable-next-line jsx-a11y/img-redundant-alt
        <img
          alt="studentImage"
          className="w-10 overflow-hidden rounded-md aspect-square"
          src={row?.image}
        />
      ),
    },
    {
      field: "name",
      headerName: "Student Name",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => <div className="capitalize"> {row?.name} </div>,
    },

    {
      field: "text",
      headerName: "Testimonial",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div className="capitalize line-clamp-2"> {row?.text} </div>
      ),
    },
    {
      field: "video",
      headerName: "Video Testimonial URL",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) =>
        row?.video ? (
          <div className="capitalize">
            <a target="_blank" rel="noopener noreferrer" href={row?.video}>
              Link
            </a>
          </div>
        ) : null,
    },
    {
      field: "currentCompany",
      headerName: "Current company",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {row?.currentCompany} </div>
      ),
    },
    {
      field: "action",
      headerName: "",
      flex: "flex-[1_1_0%]",
      extraClasses: " py-2",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];
  return (
    <div>
      <StudentTestimonialListing
        rows={items}
        columns={columns}
        getActionOptions={getActionOptions}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />
      {isOpenAddFrom ? (
        <AddStudentTestimonialFormWrapper
          onClose={() => setIsOpenAddForm(false)}
        />
      ) : null}
      {isOpenEditForm ? (
        <EditStudentTestimonialFormWrapper
          onClose={() => setIsOpenEditForm(false)}
          testimonialId={selectedId}
        />
      ) : null}
    </div>
  );
};

export default StudentTestimonialListingWrapper;
